import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * OmnibarHost — styling/layout exploration
 *
 * Mostly markup + CSS. Renders as an overlay at the bottom of the slate
 * body (mounted via `MainSlate`'s `omnibar` slot).
 *
 * Structure:
 *   - `.omnibar-host` — absolute-positioned container hugging the bottom of
 *     the slate body. Holds the focus scrim (mobile), fade/rise, the
 *     filter strip, and the bar. Carries a `data-quick-results`
 *     attribute exactly when the live "quick results" UI should be
 *     visible: Ask mode AND a non-empty query AND focus is currently
 *     inside the host. CSS keys both the strip's opacity and the rise
 *     gradient's tall height off this single attribute so the two
 *     surfaces appear and disappear in lockstep.
 *   - `.omnibar-host__focus-scrim` — mobile-only full-bleed frosted layer
 *     (matches block expanded backdrop) when the draft is active; sits above
 *     stream content but below rise/fade/pill so taps don't hit cards.
 *   - `.omnibar-host__rise` — full-width vertical white gradient. Idle at
 *     opacity 0. Grows to 40vh + opacity 1 whenever the host has focus
 *     within (`:focus-within`); grows further to 90vh while
 *     `data-quick-results` is set, so the quick-results strip reads on
 *     a dense white wash rather than a half-faded gradient.
 *   - `.omnibar-host__fade` — radial-gradient pools that mask content
 *     scrolling above the bars (and apply backdrop-blur in the same shape).
 *     Suppressed while `data-quick-results` is set so the fully-extended
 *     rise reads cleanly without a competing corner vignette.
 *   - `<OmnibarFilterStrip />` — Ask-mode mini-stream of Collections
 *     matching the live query, anchored a comfortable distance above
 *     the draft pill. Mounted while the bar is in Ask mode with a
 *     non-empty query (so its opacity-out transition can play out
 *     during blur), but its visibility (opacity) is gated on
 *     `data-quick-results` — the same attribute that drives the rise's
 *     tall mode.
 *   - `<DraftBar />` — left "Add to Stream..." pill containing the input,
 *     the collection-picker chip, and the send button.
 *   - `<SearchHint />` — right `⌘K search` affordance. Static.
 */
import React, { memo, useCallback, useState } from "react";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faUpRightAndDownLeftFromCenter, faMagnifyingGlass, } from "@fortawesome/pro-solid-svg-icons";
import { useApp } from "@/state/app-context";
import { DraftBar } from "./draft-bar";
import { OmnibarFilterStrip } from "./omnibar-filter-strip";
import "./omnibar-host.scss";
// ---------------------------------------------------------------------------
// Search hint (right)
// ---------------------------------------------------------------------------
const SearchHint = () => (_jsxs("div", { className: "omnibar-search", role: "presentation", children: [_jsx("span", { className: "omnibar-search__icon", "aria-hidden": "true", children: _jsx(FaIcon, { icon: faMagnifyingGlass }) }), _jsxs("kbd", { className: "omnibar-keycap", "aria-hidden": "true", children: [_jsx("span", { className: "omnibar-keycap__key", children: "\u2318" }), _jsx("span", { className: "omnibar-keycap__key", children: "K" })] }), _jsx("span", { className: "omnibar-search__label", children: "search" }), _jsx("span", { className: "omnibar-search__expand", "aria-hidden": "true", children: _jsx(FaIcon, { icon: faUpRightAndDownLeftFromCenter }) })] }));
// ---------------------------------------------------------------------------
// Host
// ---------------------------------------------------------------------------
export const OmnibarHost = memo(() => {
    const app = useApp();
    // The Ask-mode filter palette is the only consumer of the extended
    // rise gradient — Add mode keeps the bar lightweight so a fresh
    // capture doesn't visually take over the slate. Compute the gating
    // booleans and the trimmed query in one selector pass so the host
    // re-renders once per relevant state change.
    const trimmedQuery = app.useStore((s) => s.ui.omnibarQuery.trim());
    const isAskMode = app.useStore((s) => s.ui.omnibarMode === "ask");
    const hasAskQuery = isAskMode && trimmedQuery.length > 0;
    // Track whether focus is anywhere inside the host. React's onFocus /
    // onBlur correspond to the bubbling focusin / focusout DOM events, so
    // a single pair of handlers on the root captures focus moving onto
    // the textarea, the chip, the send button, etc. Used together with
    // `hasAskQuery` to drive `showQuickResults` — the single boolean that
    // gates both the filter strip's visibility and the rise gradient's
    // tall mode, so the two surfaces appear and disappear together.
    const [isFocused, setIsFocused] = useState(false);
    const handleFocus = useCallback(() => {
        setIsFocused(true);
    }, []);
    const handleBlur = useCallback((e) => {
        // Only flip false when focus is leaving the host entirely. Focus
        // moving between descendants (textarea → chip, etc.) reports a
        // `relatedTarget` still inside the host; bailing in that case keeps
        // the quick-results UI from flickering mid-interaction.
        const next = e.relatedTarget;
        if (next instanceof Node && e.currentTarget.contains(next))
            return;
        setIsFocused(false);
    }, []);
    // Single source of truth for "show the quick-results UI". Drives both
    // the filter strip's opacity (via the `data-quick-results` attribute
    // below + a CSS rule on the strip) and the rise gradient's tall-mode
    // height, so blurring the bar tears both surfaces back down together.
    const showQuickResults = hasAskQuery && isFocused;
    return (_jsxs("div", { className: "omnibar-host", role: "presentation", "data-quick-results": showQuickResults ? "" : undefined, onFocus: handleFocus, onBlur: handleBlur, children: [_jsx("div", { className: "omnibar-host__focus-scrim", "aria-hidden": "true" }), _jsx("div", { className: "omnibar-host__rise", "aria-hidden": "true" }), _jsx("div", { className: "omnibar-host__fade", "aria-hidden": "true" }), _jsx("div", { className: "omnibar-host__expand-scrim", "aria-hidden": "true" }), hasAskQuery ? _jsx(OmnibarFilterStrip, { query: trimmedQuery }) : null, _jsx(DraftBar, {})] }));
});
